import React, { useState, useEffect, useRef } from "react";
import { Badge, IconButton, Button, Box, Divider, Typography, Menu, MenuItem, Tooltip, Grid } from "@mui/material";
import { Delete, Close, ShoppingCart, AdminPanelSettings } from "@mui/icons-material";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { useTheme } from '@mui/material/styles';
import { styled, keyframes } from '@mui/system';
import { useAuthContext } from "../../hooks/useContext/useAuthContext";
import { useOrderContext } from "../../hooks/useContext/useOrderContext";
import ImageContainer from '../Shared/ImageContainerBox';
import SimpleAlert from '../Shared/Alert';
import { useTranslation } from '../../hooks/useTranslation';


export default function CartIconButton()  {
  const { user } = useAuthContext();
  const [showAlert, setShowAlert] = useState(false);
  const { order, totalQuantity, toggleOrderBox, dispatch} = useOrderContext();
  const theme = useTheme();
  const { t } = useTranslation();
  const deleteItemText = "Successfully deleted item!";
  const [animate, setAnimate] = useState(false);
  const prevQuantityRef = useRef(totalQuantity);

  // Detect when items are added to cart
  useEffect(() => {
    if (totalQuantity > prevQuantityRef.current) {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 600);
    }
    prevQuantityRef.current = totalQuantity;
  }, [totalQuantity]);

  const translateSize = (size) => {
    if (size === 'Small') return t('small');
    if (size === 'Large') return t('large');
    if (size === 'One Size') return t('oneSize');
    return size;
  };

 const menuItemStyle = {
    color: theme.palette.common.grey, 
    borderRadius: "10px",
    backgroundColor: theme.palette.common.white,
    padding: "5px",
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  }

  const removeItemFromOrder = (itemId, itemSize) => {
    console.log("Removing item with ID:", itemId);
    dispatch({ type: 'REMOVE_ORDER', payload: { _id: itemId, itemSize: itemSize } });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 1400)
  };

  const CustomDelete = styled(Delete)({
    color: theme.palette.common.error,
  });

  const bounce = keyframes`
    0%, 100% {
      transform: scale(1);
    }
    25% {
      transform: scale(1.2);
    }
    50% {
      transform: scale(1.1);
    }
    75% {
      transform: scale(1.15);
    }
  `;

  return (
    <PopupState variant="popover" popupId="cart-popup">
      {(popupState) => (
        <>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            
          >
            <Badge badgeContent={totalQuantity} color="error">
              <ShoppingCart
                {...bindTrigger(popupState)}
                sx={{ 
                  fontSize: 40, 
                  cursor: 'pointer', 
                  color: theme.palette.primary.main,
                  animation: animate ? `${bounce} 0.6s ease-in-out` : 'none',
                  transition: 'transform 0.3s ease'
                }}
              />
            </Badge>

            <Menu
              {...bindMenu(popupState)}
              slotProps={{
                paper: {
                  style: {
                    marginTop: '12px',
                    width: '348px',
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    borderRadius: "10px",
                  },
                },
              }}
            >
              <MenuItem onClick={popupState.close} sx={{ boxShadow: `0px 3px 2px -2px rgba(0, 0, 0, 0.2)`, backgroundColor: theme.palette.common.white, zIndex: '1', position: 'sticky', top: 0, '&.MuiMenuItem-root:hover': { backgroundColor: theme.palette.common.white }, cursor: 'default' }} >
                <Typography variant="p" gutterBottom style={{ textAlign: 'left', padding: '2%' }}>
                  <b>{t('myOrder')},</b> {order.length} {(order.length > 4 ? t('items') : (order.length === 1 ? t('item') : t('items')))}
                </Typography>

                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  aria-label="close"
                  onClick={popupState.close}
                >
                  <Close />
                </IconButton>
              </MenuItem>

              {showAlert && <SimpleAlert text={t('successfullyDeletedItem')} showAlert={showAlert} type={"success"} />}

              {order.map((cartItem, index) => (
                <div key={index}> 
                <MenuItem onClick={popupState.close} sx={menuItemStyle}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <ImageContainer sx={{ paddingRight: "20px" }} itemName={cartItem.itemName} itemImage={cartItem.itemImage} />
                  </Grid>
                  <Grid item xs={8}>
                    <Grid container direction="column" spacing={0} sx={{ paddingTop: "20px" }}>
                      <Grid item>
                        <Typography variant="p" sx={{ textAlign: "left" }}>
                          {`${cartItem.itemPrice} ${t('currency')}`}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body2" gutterBottom sx={{ textAlign: "left", overflowWrap: 'break-word', whiteSpace: 'normal', paddingRight: '10px'}}>
                          {cartItem.itemName}
                        </Typography>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" gutterBottom sx={{ textAlign: "left" }}>
                            {translateSize(cartItem.itemSize)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" gutterBottom sx={{ textAlign: "left" }}>
                            {t('qty')}: {cartItem.itemQuantity}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          sx={{ "&:hover": { backgroundColor: "#f2eeef" } }}
                          size="large"
                          aria-label="Delete"
                          aria-haspopup="true"
                          ariant="outlined"
                          onClick={(event) => {
                            event.stopPropagation(); // Prevent click from closing the menu
                            removeItemFromOrder(cartItem._id, cartItem.itemSize);
                          }}
                        >
                          <Tooltip title={t('deleteItem')} placement="top">
                            <CustomDelete />
                          </Tooltip>
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                </MenuItem>
              {index !== order.length - 1 && <Divider variant="middle" />}
              </div>
              ))}

              <MenuItem
                onClick={popupState.close}
                sx={{...menuItemStyle, position: 'sticky', bottom: 69, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '5% 6%', backgroundColor: theme.palette.common.background, '&:hover': { backgroundColor: theme.palette.common.background },
                  borderRadius: "0px"
                }}>

                <Typography variant="p" gutterBottom>
                  <b>{t('total')}:</b>
                </Typography>
                <Typography variant="p" gutterBottom>
                  {`${order.reduce((acc, item) => acc + item.itemQuantity * item.itemPrice, 0)} ${t('currency')}`}
                </Typography>
 
              </MenuItem>

              <Box sx={{
                position: 'sticky',
                bottom: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: theme.palette.common.white,
                padding: '10px 10px'
              }}>
                {order.length === 0 ? (
                  <Button 
                    href="/order"
                    onClick={toggleOrderBox}
                    variant="primaryButton"
                    sx={{
                      marginTop: "10px",
                      width: '100%'
                    }}
                  >
                    {t('viewMenu')}
                  </Button>

                ) : (
                <>
                  <Button
                    href="/cart"
                    onClick={toggleOrderBox}
                    variant="greyButton"
                    sx={{
                      // position: 'sticky',
                      // bottom: 10,
                      marginTop: "10px",
                      width: '100%',
                      marginRight: '10px'
                    }}
                  >
                    {t('viewCart')}
                  </Button>
                  <Button 
                    href="/checkout"
                    onClick={toggleOrderBox}
                    variant="primaryButton"
                    sx={{
                      // position: 'sticky',
                      // bottom: 0,
                      marginTop: "10px",
                      width: '100%'
                    }}
                  >
                    {t('checkout')}
                  </Button>
                </>
                )}
              </Box>

            </Menu>
          </IconButton>

          {user ? 
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              type="button"
              href="/dashboard"
              sx={{ marginLeft:'5px' }}
            >
              <AdminPanelSettings sx={{ fontSize: 40, cursor: 'pointer', color: theme.palette.common.black }} /> 
            </IconButton>
          : null}
        </>
      )}
  </PopupState>
  );
}